import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import EntitySelectDialog from '../../components/EntitySelectDialog/EntitySelectDialog';
import Company from '../../types/Company';
import api from '../../utils/api';
import { useDialog } from '../../utils/dialog';
import snackbar from '../../utils/snackbar';
import CompanySelectDialog from '../CompanySelectDialog/CompanySelectDialog';

/*
 * Show the list of companies of an auditor.
 */

interface Props {
    open: boolean;
    onClose: () => void;
    userId: number;
    companies: Company[];
    updateData: () => void;
    saveIfNeeded: () => Promise<void>;
    isLoading?: boolean;
}

const UserCompaniesDialog = (props: Props) => {
    const { open, onClose, userId, companies, updateData, saveIfNeeded, isLoading } = props;

    const { t } = useTranslation();

    const [isCompanySelectDialogOpen, setIsCompanySelectDialogOpen] = useState(false);
    const [openDialog, closeDialog] = useDialog();

    const [localIsLoading, setLocalIsLoading] = useState(false);

    const companyIds = companies.map(company => company.id);

    const closeCompanySelectDialog = useCallback((companyId?: number) => {
        if (companyId) {
            setLocalIsLoading(true);
            saveIfNeeded().then(() => {
                api.request('/users/' + userId + '/companies', 'POST', { companyId }).then(() => {
                    snackbar.success(t('dialogs.user_companies.company_saved'));
                    updateData();
                }).finally(() => {
                    setLocalIsLoading(false);
                });
            });
        }
        setIsCompanySelectDialogOpen(false);
    }, [t, saveIfNeeded, updateData, userId]);


    const removeCompany = useCallback((companyId: number) => {
        // function called on user/auditor delete request
        setLocalIsLoading(true);
        api.request('/users/' + userId + '/companies/' + companyId, 'DELETE').then(() => {
            snackbar.success(t('dialogs.user_companies.company_removed'));
            updateData();
        }).finally(() => {
            setLocalIsLoading(false);
        });
    }, [userId, t, updateData]);

    const deleteUserConfirm = useCallback((companyId: number) => {
        openDialog({
            title: t('dialogs.user_companies.company_remove_confirm_title'),
            message: t('dialogs.user_companies.company_remove_confirm'),
            actions: [
                {
                    text: t('dialogs.generic.cancel'),
                    color: 'secondary',
                    action: closeDialog
                },
                {
                    text: t('dialogs.generic.confirm'),
                    color: 'primary',
                    action: () => {
                        removeCompany(companyId);
                        closeDialog();
                    }
                }
            ]
        });
    }, [closeDialog, removeCompany, openDialog, t]);

    const dataForRender = useMemo(() => {
        // companies list is arranged to be used by EntitySelectDialog component
        return companies.map(company => {
            return {
                id: company.id,
                name: company.name,
                description: company.email,
                canDelete: company.canDelete
            };
        });
    }, [companies]);

    const title = t('dialogs.user_companies.title');
    const newEntityBtnText = t('dialogs.user_companies.add_company');
    const noCompanyFound = t('dialogs.user_companies.no_company_found');

    return (
        <>
            <EntitySelectDialog
                open={open}
                title={title}
                onClose={onClose}
                data={(isLoading || localIsLoading) ? [] : dataForRender}
                onDelete={deleteUserConfirm}
                newEntityBtnPosition='list'
                onNewEntityClick={() => setIsCompanySelectDialogOpen(true)}
                newEntityBtnText={newEntityBtnText}
                disableItemsClick
                isLoading={isLoading || localIsLoading}
                noEntityFoundMessage={noCompanyFound}
            />
            <CompanySelectDialog open={isCompanySelectDialogOpen} onClose={closeCompanySelectDialog} excludedIds={companyIds} />
        </>
    );
};

export default UserCompaniesDialog;
